import React, { useCallback, useContext, useEffect, useState } from "react";
import { Feature } from "geojson";

import { HistoryContext, useHistory } from "fond/history";
import { getHotkeyLabel, useHotkeys } from "fond/hooks/useHotkeys";
import { MapContext } from "fond/map/MapProvider";
import { useHasEditableSelection } from "fond/map/Toolbar";
import { IButtonProps } from "fond/map/Toolbar/Toolbar";
import ToolbarButton from "fond/map/Toolbar/ToolbarButton";
import { MuPowerSolverLayerIds } from "fond/types";

import { PhaseNetwork } from "./phaseNetwork";
import { isSinglePhase, OverridePhase } from "./phases";

interface IProps extends IButtonProps {
  phase: OverridePhase;
  network: PhaseNetwork;
}

/**
 * Sets the Phase of all selected phase override points to the button's phase.
 */
const PhaseButton: React.FC<IProps> = ({ phase, network, title }) => {
  const { map, drawControl } = useContext(MapContext);
  const { set: setHistory } = useHistory(HistoryContext);
  const [commonPhase, setCommonPhase] = useState<unknown>(null);
  const [isAllowed, setIsAllowed] = useState(false);
  const hasEditableSelection = useHasEditableSelection();

  const getOverridePoints = useCallback(
    (): Feature[] =>
      (drawControl.current?.getAll().features ?? []).filter((feature) => feature.properties?.layerId === MuPowerSolverLayerIds.phaseOverrides),
    [drawControl]
  );

  /**
   * If the user is selecting a single feature (or a set of features that all share
   * the same phase), then highlight the button for that phase. If the user is selecting multiple features
   * with different phases, then nothing is highlighted.
   *
   * The button is only enabled when its phase is allowed at every selected override point.
   */
  const handleOnSelect = useCallback(() => {
    const features = drawControl.current?.getSelected().features ?? [];
    const phases = new Set(features.map((feature) => feature.properties?.Phase));
    setCommonPhase(phases.size === 1 ? [...phases][0] : null);
    setIsAllowed(network.allowedPhasesForSelection(features, getOverridePoints()).has(phase));
  }, [drawControl, getOverridePoints, network, phase]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleOnClick = useCallback(() => {
    const selectedFeatures = drawControl.current?.getSelected().features ?? [];
    if (selectedFeatures.length === 0) return;

    network.phaseEdits(selectedFeatures, getOverridePoints(), phase).forEach((newPhase, id) => {
      drawControl.current?.setFeatureProperty(String(id), "Phase", newPhase);
    });

    const allFeatures = drawControl.current?.getAll();
    if (allFeatures) {
      setHistory(allFeatures);
    }

    // ensure the other toolbar buttons are updated to reflect the new phase of the selected features
    map?.fire("draw.selectionchange", { type: "draw.selectionchange", features: drawControl.current?.getSelected().features ?? [], points: [] });
  }, [drawControl, getOverridePoints, map, network, phase, setHistory]);

  const hotkeyAction = isSinglePhase(phase) ? (`PHASE_${phase}` as const) : undefined;

  useHotkeys({
    action: hotkeyAction,
    callback: (event) => {
      event.preventDefault();
      handleOnClick();
    },
    enabled: hasEditableSelection && isAllowed,
  });

  const tooltipTitle = `${title ?? `Set phase ${phase}`}${hotkeyAction ? ` (${getHotkeyLabel(hotkeyAction)})` : ""}`;

  return (
    <ToolbarButton
      title={tooltipTitle}
      onClick={handleOnClick}
      disabled={!hasEditableSelection || !isAllowed}
      active={commonPhase === phase}
      data-testid={`toolbar-phase-${phase.toLowerCase()}-button`}
    >
      {phase}
    </ToolbarButton>
  );
};

export default PhaseButton;
